<template>
  <div class="sync-reference-page">
    <div class="sync-reference-panel bg-white shadow-sm rounded">
      <div class="d-flex flex-wrap justify-content-between align-items-center mb-3">
        <div>
          <h4 class="mb-1">
            Correction références
          </h4>
          <small class="text-muted">
            Modifier les références externes des colis déjà synchronisés.
          </small>
        </div>
        <b-badge
          variant="light-primary"
          class="mt-2 mt-md-0"
        >
          {{ orders.length }} résultat(s)
        </b-badge>
      </div>

      <b-alert
        :show="Boolean(errorMessage)"
        variant="danger"
        dismissible
        @dismissed="errorMessage = ''"
      >
        {{ errorMessage }}
      </b-alert>

      <b-form @submit.prevent="searchOrders">
        <div class="d-flex flex-wrap align-items-end mb-3">
          <b-form-group
            label="Référence colis"
            label-for="sync-reference-search"
            class="mr-2 mb-2 flex-grow-1"
          >
            <b-input-group class="input-group-merge">
              <b-input-group-prepend is-text>
                <feather-icon icon="SearchIcon" />
              </b-input-group-prepend>
              <b-form-input
                id="sync-reference-search"
                ref="searchInput"
                v-model="search"
                type="search"
                placeholder="BL, external_id ou barcode"
                autocomplete="off"
              />
            </b-input-group>
          </b-form-group>
          <b-button
            type="submit"
            variant="primary"
            class="mb-2"
            :disabled="loading"
          >
            <feather-icon
              icon="SearchIcon"
              class="mr-1"
            />
            Rechercher
          </b-button>
        </div>
      </b-form>

      <b-overlay
        :show="loading"
        rounded
        opacity="0.65"
      >
        <b-table
          :items="orders"
          :fields="tableFields"
          responsive
          hover
          show-empty
          empty-text="Aucun colis trouvé."
          class="sync-reference-table mb-0"
        >
          <template #cell(id)="row">
            <router-link :to="{ name: 'order-show', params: { id: row.item.id } }">
              <strong>{{ row.item.id }}</strong>
            </router-link>
            <small class="text-muted d-block">{{ formatDate(row.item.created_at) }}</small>
          </template>

          <template #cell(phone)="row">
            {{ row.item.phone1 || '-' }}
            <small
              v-if="row.item.phone2"
              class="text-muted d-block"
            >
              {{ row.item.phone2 }}
            </small>
          </template>

          <template #cell(outsourcing_company)="row">
            {{ outsourcingCompanyName(row.item) }}
          </template>

          <template #cell(external_id)="row">
            <code>{{ row.item.external_id || '-' }}</code>
          </template>

          <template #cell(external_id_2)="row">
            <code>{{ row.item.external_id_2 || '-' }}</code>
          </template>

          <template #cell(actions)="row">
            <b-button
              :variant="hasSynchronizedPartner(row.item) ? 'outline-primary' : 'outline-warning'"
              size="sm"
              class="bg-white"
              @click="openEditor(row.item)"
            >
              <feather-icon
                icon="Edit3Icon"
                class="mr-1"
              />
              Modifier
            </b-button>
          </template>
        </b-table>
      </b-overlay>
    </div>

    <b-modal
      v-model="editorVisible"
      title="Modifier références"
      hide-footer
      centered
      @hidden="resetEditor"
    >
      <div v-if="selectedOrder">
        <div class="mb-2">
          <strong>Colis {{ selectedOrder.id }}</strong>
          <small class="text-muted d-block">{{ selectedOrder.name || '-' }}</small>
        </div>

        <b-alert
          :show="Boolean(saveError)"
          variant="danger"
          dismissible
          @dismissed="saveError = ''"
        >
          {{ saveError }}
        </b-alert>

        <b-form @submit.prevent="saveReferences">
          <b-form-group
            label="Référence (BL) 1"
            label-for="sync-reference-external-id"
          >
            <b-form-input
              id="sync-reference-external-id"
              v-model="form.external_id"
              autocomplete="off"
              maxlength="255"
            />
          </b-form-group>

          <b-form-group
            label="Référence (BL) 2"
            label-for="sync-reference-external-id-2"
          >
            <b-form-input
              id="sync-reference-external-id-2"
              v-model="form.external_id_2"
              autocomplete="off"
              maxlength="255"
            />
          </b-form-group>

          <div class="d-flex justify-content-end">
            <b-button
              variant="outline-secondary"
              class="mr-2 bg-white"
              :disabled="saving"
              @click="editorVisible = false"
            >
              Annuler
            </b-button>
            <b-button
              type="submit"
              variant="primary"
              :disabled="saving"
            >
              <b-spinner
                v-if="saving"
                small
                class="mr-1"
              />
              Enregistrer
            </b-button>
          </div>
        </b-form>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BAlert,
  BBadge,
  BButton,
  BForm,
  BFormGroup,
  BFormInput,
  BInputGroup,
  BInputGroupPrepend,
  BModal,
  BOverlay,
  BSpinner,
  BTable,
} from 'bootstrap-vue'
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    BAlert,
    BBadge,
    BButton,
    BForm,
    BFormGroup,
    BFormInput,
    BInputGroup,
    BInputGroupPrepend,
    BModal,
    BOverlay,
    BSpinner,
    BTable,
  },
  data() {
    return {
      search: '',
      loading: false,
      saving: false,
      errorMessage: '',
      saveError: '',
      orders: [],
      selectedOrder: null,
      editorVisible: false,
      form: {
        external_id: '',
        external_id_2: '',
      },
      tableFields: [
        { key: 'id', label: 'Commande' },
        { key: 'name', label: 'Client' },
        { key: 'phone', label: 'Téléphone' },
        { key: 'status', label: 'Statut' },
        { key: 'outsourcing_company', label: 'Partenaire' },
        { key: 'external_id', label: 'Référence (BL) 1' },
        { key: 'external_id_2', label: 'Référence (BL) 2' },
        { key: 'actions', label: '', class: 'text-right' },
      ],
    }
  },
  mounted() {
    this.$nextTick(() => {
      if (this.$refs.searchInput) {
        this.$refs.searchInput.focus()
      }
    })
  },
  methods: {
    async searchOrders() {
      const query = this.search.trim()
      if (!query) {
        this.errorMessage = 'Saisissez une référence à rechercher.'
        return
      }

      this.loading = true
      this.errorMessage = ''
      try {
        const { data } = await axios.get('/api/orders/admin-search/', {
          params: { bl: query },
        })
        this.orders = Array.isArray(data) ? data : []
        const unsynchronizedOrder = this.orders.find(order => !this.hasSynchronizedPartner(order))
        if (unsynchronizedOrder) {
          this.handleUnsynchronizedOrder({ speak: true })
        }
      } catch (error) {
        this.errorMessage = this.extractError(error, 'Recherche impossible.')
      } finally {
        this.loading = false
      }
    },
    openEditor(order) {
      if (!this.hasSynchronizedPartner(order)) {
        this.handleUnsynchronizedOrder({ speak: false })
        return
      }

      this.selectedOrder = order
      this.form = {
        external_id: order.external_id || '',
        external_id_2: order.external_id_2 || '',
      }
      this.saveError = ''
      this.editorVisible = true
    },
    hasSynchronizedPartner(order) {
      return Boolean(order && order.outsourcing_company)
    },
    handleUnsynchronizedOrder({ speak = false } = {}) {
      const message = "Impossible, ce colis n'est pas synchronisé."
      this.errorMessage = message
      this.showToast('warning', message)
      if (speak) {
        this.speakWarning(message)
      }
    },
    async saveReferences() {
      if (!this.selectedOrder) return

      this.saving = true
      this.saveError = ''
      try {
        const payload = {
          external_id: this.form.external_id,
          external_id_2: this.form.external_id_2,
        }
        const { data } = await axios.patch(
          `/api/orders/sync-reference-correction/${this.selectedOrder.id}/`,
          payload,
        )

        this.replaceOrder(data.order)
        this.editorVisible = false
        this.showToast('success', data.message || 'Références mises à jour.')
      } catch (error) {
        this.saveError = this.extractError(error, 'Modification impossible.')
      } finally {
        this.saving = false
      }
    },
    replaceOrder(updatedOrder) {
      if (!updatedOrder) return
      const index = this.orders.findIndex(order => order.id === updatedOrder.id)
      if (index === -1) return
      this.$set(this.orders, index, {
        ...this.orders[index],
        ...updatedOrder,
      })
    },
    resetEditor() {
      this.selectedOrder = null
      this.saveError = ''
      this.form = {
        external_id: '',
        external_id_2: '',
      }
    },
    outsourcingCompanyName(order) {
      if (!order.outsourcing_company) return '-'
      return order.outsourcing_company.name || order.outsourcing_company.display_name || '-'
    },
    formatDate(value) {
      if (!value) return '-'
      return new Date(value).toLocaleString('fr-FR')
    },
    extractError(error, fallback) {
      const data = error && error.response ? error.response.data : null
      if (!data) return fallback
      if (typeof data === 'string') return data
      if (data.detail) return data.detail
      if (data.error) return data.error

      const firstKey = Object.keys(data)[0]
      if (!firstKey) return fallback
      const value = data[firstKey]
      if (Array.isArray(value)) return value.join(' ')
      return String(value)
    },
    showToast(variant, text) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: text,
          icon: variant === 'success' ? 'CheckCircleIcon' : 'AlertTriangleIcon',
          variant,
        },
      })
    },
    speakWarning(text) {
      if (!text || !('speechSynthesis' in window)) return

      const utterance = new SpeechSynthesisUtterance(text)
      utterance.lang = 'fr-FR'
      utterance.rate = 0.95
      window.speechSynthesis.cancel()
      window.speechSynthesis.speak(utterance)
    },
  },
}
</script>

<style scoped>
.sync-reference-panel {
  padding: 1.25rem;
}

.sync-reference-table code {
  color: #5e5873;
  font-size: 0.85rem;
  white-space: nowrap;
}
</style>
